/**
 * @deprecated
 * Used to get things from the angular app without being in it.
 *
 * ```
 *   import ng from 'modules/angularThings';
 *
 *   ...
 *
 *   ng.$state.href('start');
 * ```
 *
 * Before angular is ready
 * ------------------------
 * If your code could be called beore the app has angular booted up,
 * you'll to wait until angular is ready using the onAngularReady promise.
 * ```
 *   import ng from 'modules/angularThings';
 *
 *   ...
 *
 *   ng.onAngularReady.then(() => {
 *     var user = ng.$injector.get('User');
 *   });
 * ```
 */

// eslint-disable-next-line import/no-mutable-exports
let angularThings = null;

/**
 * These callback functions will be sent straight to angular's .run
 * immediately when the app is ready to be initialized.
 * You should use the "onAngularReady" unless you need your code
 * executed in the same cycle as angular's initialization
 */
const onAppReadyListeners = [];

// Create deferred promise for onAngularReady
let ngReadyResolver;
const onAngularReady = new Promise(resolve => {
  ngReadyResolver = resolve;
});

/**
 * When angular is available,
 * if angular is not yet loaded it will resolve a promise with the requested angular modules.
 * otherwise return the modules immediately
 * @param moduleNames
 * @returns {Promise}
 */
const getInjectables = (...moduleNames) => (
  onAngularReady.then(() => (
    moduleNames.map(name => angularThings.$injector.get(name))
  ))
);

/**
 * Any callback passed into this method gets called when our angular app is ready
 * This should only be used if your callback needs to be executed in the same tick
 * (promises would wait until the next cycle).
 * @param callbackFn
 */
const onAppReady = callbackFn => {
  if (angularThings.app) {
    callbackFn(angularThings.app);
    return;
  }

  onAppReadyListeners.push(callbackFn);
};

/**
 * Initializes the "angularThings" bridge.
 * @param {angular.IModule} app
 */
const init = app => {
  app.run(['$injector', '$state', '$stateParams', ($injector, $state, $stateParams) => {
    Object.assign(angularThings, {
      app,
      $injector,
      $state,
      $stateParams,
    });

    ngReadyResolver(angularThings);
  }]);

  // call any "app ready" listeners
  (onAppReadyListeners || []).forEach(listener => listener(app));
};

// Base export
angularThings = {
  init,
  app: null,
  onAngularReady,
  onAppReady,
  getInjectables,
  $injector: null,
  $state: null,
  $stateParams: {},
};

export default angularThings;

// Make this available to the unit tests global.js file
// #if process.env.NODE_ENV === 'test'
// window.onAngularReady = onAngularReady;
// #endif
